/* ==========================================================================
   ET Product Compare (FAT-257, pool loading reworked in FAT-298)
   Everything is driven by the bike pool collection: auto-populated columns
   are its first bikes of the current product's type (collection sort
   order, e.g. best-selling), picked in Liquid and embedded with the page —
   specs and per-market prices can only be produced server-side. The
   "choose your bike" drawer lists the pool's vendors (logos from the merk
   metaobject) and bikes; that pool is fetched as JSON from
   ?view=et-compare-pool on first open (or on hover of the trigger), never
   on page load, and filtered client-side from then on. Picking switches to
   manual mode (auto columns are cleared) so the visitor composes their own
   comparison of up to 4 bikes (current product + 3 picked).
   ========================================================================== */
(function () {
  'use strict';

  function initSection(section) {
    if (section.__etCompareInit) return;
    section.__etCompareInit = true;

    var dataEl = section.querySelector('[data-compare-data]');
    var scroller = section.querySelector('[data-compare-scroll]');
    var slotsWrap = section.querySelector('[data-compare-slots]');
    var slotTemplate = section.querySelector('template[data-compare-slot]');
    var track = section.querySelector('[data-compare-scrollbar]');
    var thumb = section.querySelector('[data-compare-scrollbar-thumb]');
    var prevBtn = section.querySelector('[data-compare-prev]');
    var nextBtn = section.querySelector('[data-compare-next]');
    if (!dataEl || !scroller || !slotsWrap || !slotTemplate) return;

    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var byHandle = {};
    (config.autoCandidates || []).forEach(function (candidate) {
      byHandle[candidate.handle] = candidate;
    });

    var rows = Array.prototype.slice.call(section.querySelectorAll('[data-compare-row]'));
    var state = { mode: 'auto', added: [] };

    /* --- Extra spec rows (FAT-382) -----------------------------------------
       Only the first five spec rows are shown; the other ~15 metafields sit
       behind "show more". No bike fills all of them, and a row that is empty
       for every column on screen says nothing about the bikes being compared,
       so it is dropped rather than shown as a line of dashes — this is what
       keeps the expanded table down to the specs that actually differ. Row
       titles are siblings of their row (mobile makes them sticky inside the
       scroller), so the pairing goes through the row index. */

    var extraRows = Array.prototype.slice.call(
      section.querySelectorAll('.et-product-compare__row[data-compare-extra]')
    );
    var specsToggle = section.querySelector('[data-compare-specs-toggle]');
    var specsToggleText = section.querySelector('[data-compare-specs-toggle-text]');

    function rowIsEmpty(row) {
      return Array.prototype.every.call(
        row.querySelectorAll('.et-product-compare__cell'),
        function (cell) {
          var text = cell.textContent.trim();
          return text === '' || text === '—';
        }
      );
    }

    function syncExtraRows() {
      var visible = 0;
      extraRows.forEach(function (row) {
        var empty = rowIsEmpty(row);
        row.hidden = empty;
        var title = section.querySelector(
          '.et-product-compare__row-title[data-spec-title="' +
            row.getAttribute('data-spec-index') +
            '"]'
        );
        if (title) title.hidden = empty;
        if (!empty) visible++;
      });
      if (specsToggle) specsToggle.hidden = visible === 0;
      // Drives the collapsed table's bottom fade: with nothing left to
      // reveal it would promise rows that aren't there.
      section.setAttribute('data-has-extra', visible > 0 ? 'true' : 'false');
    }

    if (specsToggle) {
      specsToggle.addEventListener('click', function () {
        var expanded = section.getAttribute('data-specs-expanded') === 'true';
        var next = expanded ? 'false' : 'true';
        section.setAttribute('data-specs-expanded', next);
        specsToggle.setAttribute('aria-expanded', next);
        if (specsToggleText) {
          specsToggleText.textContent = expanded
            ? specsToggle.getAttribute('data-show-text')
            : specsToggle.getAttribute('data-hide-text');
        }
        // Collapsing pulls a screenful of rows out from under the visitor, so
        // the page shortens beneath them and they end up somewhere past the
        // section. Put them back at its heading. The sticky header is already
        // covered by the theme's html { scroll-padding-top } — adding a
        // scroll-margin here as well just doubles the offset.
        if (expanded) {
          var heading = section.querySelector('.et-product-compare__title');
          if (heading) heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    /* --- Column building --------------------------------------------------- */

    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text) node.textContent = text;
      return node;
    }

    function buildRemoveButton(handle) {
      var removeBtn = el('button', 'et-product-compare__remove');
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', config.labels.remove);
      removeBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>';
      removeBtn.addEventListener('click', function () {
        removeColumn(handle);
      });
      return removeBtn;
    }

    function buildCell(row, candidate, isAuto) {
      var type = row.getAttribute('data-compare-row');
      var cell;

      if (type === 'head') {
        cell = el('div', 'et-product-compare__cell et-product-compare__product');
        if (!isAuto) {
          cell.appendChild(buildRemoveButton(candidate.handle));
        }
        var link = el('a', 'et-product-compare__product-link');
        link.href = candidate.url;
        if (candidate.image) {
          var img = el('img', 'et-product-compare__image');
          img.src = candidate.image;
          img.alt = candidate.imageAlt || candidate.title;
          img.loading = 'lazy';
          link.appendChild(img);
        }
        link.appendChild(el('span', 'et-product-compare__product-title', candidate.title));
        cell.appendChild(link);
      } else if (type === 'price') {
        cell = el('div', 'et-product-compare__cell et-product-compare__price-cell');
        var pricing = el('span', 'et-product-compare__pricing');
        pricing.appendChild(el('span', 'et-product-compare__price', candidate.price));
        if (candidate.comparePrice) {
          pricing.appendChild(el('s', 'et-product-compare__compare-price', candidate.comparePrice));
        }
        cell.appendChild(pricing);
        var cta = el('a', 'et-product-compare__cta', config.labels.viewCta);
        cta.href = candidate.url;
        cell.appendChild(cta);
      } else {
        var index = parseInt(row.getAttribute('data-spec-index'), 10) || 0;
        cell = el('div', 'et-product-compare__cell', candidate.specs[index] || '—');
      }

      cell.setAttribute('data-compare-col', candidate.handle);
      if (isAuto) cell.setAttribute('data-compare-auto', 'true');
      return cell;
    }

    function addColumn(candidate, isAuto) {
      rows.forEach(function (row) {
        var cell = buildCell(row, candidate, isAuto);
        if (row.getAttribute('data-compare-row') === 'head') {
          row.insertBefore(cell, slotsWrap);
        } else {
          row.appendChild(cell);
        }
      });
      state.added.push({ handle: candidate.handle, auto: isAuto });
      refresh();
    }

    function removeColumn(handle) {
      section.querySelectorAll('[data-compare-col="' + handle + '"]').forEach(function (cell) {
        cell.remove();
      });
      state.added = state.added.filter(function (entry) {
        return entry.handle !== handle;
      });
      refresh();
    }

    function clearAutoColumns() {
      section.querySelectorAll('[data-compare-auto="true"]').forEach(function (cell) {
        cell.remove();
      });
      state.added = state.added.filter(function (entry) {
        return !entry.auto;
      });
    }

    /* --- "Choose your bike" picker (manual mode) --------------------------- */

    function enterManualMode() {
      if (state.mode === 'auto') {
        // First manual pick switches to manual mode: auto slots go blank.
        clearAutoColumns();
        state.mode = 'manual';
      }
    }

    function onPick(handle, url) {
      var alreadyAdded = state.added.some(function (entry) {
        return entry.handle === handle;
      });
      if (alreadyAdded || 1 + state.added.length >= config.maxColumns) return;

      var candidate = byHandle[handle];
      if (candidate) {
        enterManualMode();
        addColumn(candidate, false);
        return;
      }
      if (url) addRemoteColumn(handle, url);
    }

    /* Bikes outside the embedded pool (store-wide search results): render
       this section for the picked product via the Section Rendering API and
       graft its "current product" cells in as a new column — full metafield
       specs and market prices in one request. */
    function addRemoteColumn(handle, url) {
      var renderUrl;
      try {
        renderUrl = new URL(url, window.location.origin);
      } catch (e) {
        return;
      }
      renderUrl.search = '';
      renderUrl.searchParams.set('section_id', section.getAttribute('data-section-id'));

      section.classList.add('et-product-compare--busy');
      fetch(renderUrl.toString())
        .then(function (response) {
          return response.ok ? response.text() : Promise.reject();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fetchedRows = doc.querySelectorAll('[data-compare-row]');
          if (fetchedRows.length !== rows.length) return;

          enterManualMode();
          rows.forEach(function (row, index) {
            var sourceCell = fetchedRows[index].querySelector('.et-product-compare__cell--current');
            if (!sourceCell) return;
            var cell = document.importNode(sourceCell, true);
            cell.classList.remove('et-product-compare__cell--current');
            var badge = cell.querySelector('.et-product-compare__badge');
            if (badge) badge.remove();
            cell.setAttribute('data-compare-col', handle);
            if (row.getAttribute('data-compare-row') === 'head') {
              cell.insertBefore(buildRemoveButton(handle), cell.firstChild);
              row.insertBefore(cell, slotsWrap);
            } else {
              row.appendChild(cell);
            }
          });
          state.added.push({ handle: handle, auto: false });
          refresh();
        })
        .catch(function () {})
        .finally(function () {
          section.classList.remove('et-product-compare--busy');
        });
    }

    function renderSlot() {
      slotsWrap.innerHTML = '';
      var total = 1 + state.added.length;
      if (total >= config.maxColumns) return;

      var slot = slotTemplate.content.firstElementChild.cloneNode(true);
      var openBtn = slot.querySelector('[data-compare-open-drawer]');
      if (openBtn) {
        openBtn.addEventListener('click', openDrawer);
        // Intent signals: start the pool request before the click lands, so
        // the drawer usually opens onto finished tiles
        openBtn.addEventListener('pointerenter', loadPool);
        openBtn.addEventListener('focus', loadPool);
      }
      slotsWrap.appendChild(slot);
    }

    /* --- "Choose your bike" side drawer: brands and bikes come from the
       bike pool collection, fetched as JSON on first open (or on hover of
       the trigger) and rendered client-side; filtering is local --- */

    var drawer = section.querySelector('[data-compare-drawer]');
    var drawerOverlay = section.querySelector('[data-compare-drawer-overlay]');
    var drawerCloseBtn = section.querySelector('[data-compare-drawer-close]');
    var drawerSearch = section.querySelector('[data-compare-search]');
    var drawerBrands = section.querySelector('[data-compare-brands]');
    var drawerResults = section.querySelector('[data-compare-results]');
    var drawerBikes = section.querySelector('[data-compare-bikes]');
    var drawerBackBtn = section.querySelector('[data-compare-back]');
    var drawerBackLabel = section.querySelector('[data-compare-back-label]');
    var drawerEmpty = section.querySelector('[data-compare-empty]');
    var drawerSearchResults = section.querySelector('[data-compare-search-results]');
    var drawerSpinner = section.querySelector('[data-compare-spinner]');
    var drawerLoading = section.querySelector('[data-compare-loading]');
    var searchUrl = section.getAttribute('data-search-url') || '/search';
    var bikeTypes = (config.bikeTypes || []).map(function (type) {
      return type.trim().toLowerCase();
    });
    var backLabelDefault = drawerBackLabel ? drawerBackLabel.textContent : '';
    var currentMatcher = null;
    var searchTimer;
    var activeQueryId = 0;

    // Queried live: pool pages 2+ merge extra tiles in after load
    function getBikeTiles() {
      return Array.prototype.slice.call(section.querySelectorAll('[data-compare-pick]'));
    }

    function filterBikeTiles(matcher) {
      currentMatcher = matcher;
      var visible = 0;
      getBikeTiles().forEach(function (tile) {
        var addedAlready = state.added.some(function (entry) {
          return entry.handle === tile.getAttribute('data-compare-pick');
        });
        var show = !addedAlready && matcher(tile);
        tile.hidden = !show;
        if (show) visible++;
      });
      if (drawerEmpty) drawerEmpty.hidden = visible > 0;
    }

    /* --- Bike pool: fetched on first open, never on page load (FAT-298).
       The pool used to ship with every bike PDP as ~250 hidden tiles plus a
       specs/prices blob; now /collections/{pool}?view=et-compare-pool
       returns the same data as JSON, paginated by 250. --- */

    var poolPromise = null;
    var tiledHandles = {};
    // Pool items in arrival order — the auto-column hydration picks from
    // this the way the old Liquid pass picked from the paginated pool.
    var poolOrder = [];

    function isPoolBike(item) {
      if (item.handle === config.currentHandle) return false;
      if (!bikeTypes.length) return true;
      return bikeTypes.indexOf((item.type || '').trim().toLowerCase()) !== -1;
    }

    function buildBikeTile(item) {
      var tile = el('button', 'et-product-compare__drawer-bike');
      tile.type = 'button';
      tile.setAttribute('data-compare-pick', item.handle);
      tile.setAttribute('data-url', item.url);
      tile.setAttribute('data-vendor', item.vendor || '');
      tile.setAttribute('data-title', (item.title || '').toLowerCase());
      var thumb = item.thumb || item.image;
      if (thumb) {
        var img = el('img', 'et-product-compare__drawer-bike-img');
        img.src = thumb;
        img.alt = item.imageAlt || item.title;
        img.loading = 'lazy';
        img.width = 72;
        img.height = 72;
        tile.appendChild(img);
      }
      var info = el('span', 'et-product-compare__drawer-bike-info');
      info.appendChild(el('span', 'et-product-compare__drawer-bike-title', item.title));
      var priceWrap = el('span', 'et-product-compare__drawer-bike-price');
      priceWrap.appendChild(el('span', 'et-product-compare__price', item.price));
      if (item.comparePrice) {
        priceWrap.appendChild(el('s', 'et-product-compare__compare-price', item.comparePrice));
      }
      info.appendChild(priceWrap);
      tile.appendChild(info);
      return tile;
    }

    function buildBrandTile(item) {
      var tile = el('button', 'et-product-compare__drawer-brand');
      tile.type = 'button';
      tile.setAttribute('data-compare-brand', item.vendor);
      if (item.vendorLogo) {
        var logo = el('img', 'et-product-compare__drawer-brand-logo');
        logo.src = item.vendorLogo;
        logo.alt = item.vendor;
        logo.loading = 'lazy';
        tile.appendChild(logo);
      } else {
        tile.appendChild(el('span', 'et-product-compare__drawer-brand-name', item.vendor));
      }
      return tile;
    }

    /* Appends one pool page. Brands keep first-occurrence order, so they
       rank by their best-selling bike's position in the pool — a brand that
       first appears on page N belongs below every brand seen before it. */
    function appendPoolPage(products) {
      var knownBrands = {};
      if (drawerBrands) {
        Array.prototype.forEach.call(drawerBrands.children, function (tile) {
          knownBrands[tile.getAttribute('data-compare-brand')] = true;
        });
      }

      products.forEach(function (item) {
        // byHandle already holds the auto-column bikes — they still belong
        // in the drawer, so dedupe on the tiles instead
        if (!isPoolBike(item) || tiledHandles[item.handle]) return;
        tiledHandles[item.handle] = true;
        byHandle[item.handle] = item;
        poolOrder.push(item);
        if (drawerBikes) drawerBikes.appendChild(buildBikeTile(item));
        if (drawerBrands && item.vendor && !knownBrands[item.vendor]) {
          knownBrands[item.vendor] = true;
          drawerBrands.appendChild(buildBrandTile(item));
        }
      });

      maybeFillAuto(false);

      // Fresh tiles arrive unfiltered — re-apply the active brand filter if
      // the visitor is already looking at results
      if (drawerResults && !drawerResults.hidden && currentMatcher) {
        filterBikeTiles(currentMatcher);
      }
    }

    function showPoolLoading(isLoading) {
      if (drawerLoading) {
        drawerLoading.hidden = !isLoading;
        if (isLoading && !drawerLoading.children.length) {
          for (var i = 0; i < 4; i++) drawerLoading.appendChild(buildSkeleton());
        }
      }
      // Brand tiles are empty until the pool lands; skeletons stand in
      if (drawerBrands && (drawerResults ? drawerResults.hidden : true)) {
        drawerBrands.hidden = isLoading;
      }
    }

    function fetchPoolPage(page) {
      var url = config.poolUrl + (page > 1 ? '&page=' + page : '');
      return fetch(url).then(function (response) {
        return response.ok ? response.json() : Promise.reject();
      });
    }

    function loadPool() {
      if (poolPromise) return poolPromise;

      // Fallback mode (merchant-picked list): the bikes came with the page
      if (config.poolProducts) {
        appendPoolPage(config.poolProducts);
        poolPromise = Promise.resolve();
        return poolPromise;
      }
      if (!config.poolUrl) return Promise.resolve();

      showPoolLoading(true);
      poolPromise = fetchPoolPage(1)
        .then(function (data) {
          appendPoolPage(data.products || []);
          showPoolLoading(false);
          var rest = [];
          for (var page = 2; page <= (data.pages || 1); page++) {
            rest.push(
              fetchPoolPage(page)
                .then(function (pageData) {
                  appendPoolPage(pageData.products || []);
                })
                .catch(function () {})
            );
          }
          return Promise.all(rest);
        })
        .catch(function () {
          showPoolLoading(false);
          // A failed prefetch stays silent; a failed open says so instead
          // of showing an empty drawer
          if (drawerEmpty && drawer && drawer.classList.contains('is-open')) {
            drawerEmpty.hidden = false;
            showResults('pool');
          }
          // Let the next open retry
          poolPromise = null;
        });
      return poolPromise;
    }

    function showBrandGrid() {
      var poolLoading = drawerLoading && !drawerLoading.hidden;
      if (drawerBrands) drawerBrands.hidden = poolLoading;
      if (drawerResults) drawerResults.hidden = true;
      if (drawerBackLabel) drawerBackLabel.textContent = backLabelDefault;
      if (drawerSearchResults) drawerSearchResults.innerHTML = '';
      if (drawerEmpty) drawerEmpty.hidden = true;
    }

    // mode 'pool': server-rendered pool tiles (brand browsing);
    // mode 'search': client-rendered store-wide results
    function showResults(mode) {
      if (drawerBrands) drawerBrands.hidden = true;
      if (drawerResults) drawerResults.hidden = false;
      if (drawerBikes) drawerBikes.hidden = mode === 'search';
      if (drawerSearchResults) drawerSearchResults.hidden = mode !== 'search';
    }

    /* --- Store-wide search: custom JSON view (templates/search.et-compare
       .liquid), up to 50 results with per-market server-formatted prices —
       any brand or model is comparable, not just the pool --- */

    function isComparableResult(product) {
      if (product.handle === config.currentHandle) return false;
      var added = state.added.some(function (entry) {
        return entry.handle === product.handle;
      });
      if (added) return false;
      if (!bikeTypes.length) return true;
      return bikeTypes.indexOf((product.type || '').trim().toLowerCase()) !== -1;
    }

    /* Shopify relevance ranks description matches above title matches (a
       search for a model name can surface other bikes first), so re-rank:
       title-word prefix > title substring > vendor, body-only matches last.
       Native sort is stable, ties keep Shopify's relevance order. */
    function scoreResult(product, terms) {
      var title = (product.title || '').toLowerCase();
      var vendor = (product.vendor || '').toLowerCase();
      var titleWords = title.split(/[^a-z0-9]+/);
      var score = 0;
      terms.forEach(function (term) {
        if (
          titleWords.some(function (word) {
            return word.indexOf(term) === 0;
          })
        ) {
          score += 4;
        } else if (title.indexOf(term) !== -1) {
          score += 3;
        } else if (vendor.indexOf(term) !== -1) {
          score += 2;
        }
      });
      return score;
    }

    function renderSearchResults(products, query) {
      drawerSearchResults.innerHTML = '';
      var shown = products.filter(isComparableResult);
      var terms = (query || '').toLowerCase().split(/\s+/).filter(Boolean);
      if (terms.length) {
        shown.sort(function (a, b) {
          return scoreResult(b, terms) - scoreResult(a, terms);
        });
      }
      shown.forEach(function (product) {
        drawerSearchResults.appendChild(buildBikeTile(product));
      });
      if (drawerEmpty) drawerEmpty.hidden = shown.length > 0;
    }

    function buildSkeleton() {
      var skeleton = el('div', 'et-product-compare__drawer-skeleton');
      skeleton.appendChild(el('span', 'et-product-compare__drawer-skeleton-img'));
      var lines = el('span', 'et-product-compare__drawer-skeleton-lines');
      lines.appendChild(el('span', 'et-product-compare__drawer-skeleton-line'));
      lines.appendChild(
        el('span', 'et-product-compare__drawer-skeleton-line et-product-compare__drawer-skeleton-line--short')
      );
      skeleton.appendChild(lines);
      return skeleton;
    }

    /* Skeleton tiles fill the search results while the request runs */
    function showSkeleton() {
      if (!drawerSearchResults) return;
      drawerSearchResults.innerHTML = '';
      if (drawerEmpty) drawerEmpty.hidden = true;
      for (var i = 0; i < 4; i++) {
        drawerSearchResults.appendChild(buildSkeleton());
      }
    }

    function fetchCatalog(query) {
      var queryId = ++activeQueryId;
      // options[prefix]=last: partially typed last word still matches
      // ("zipp" finds "Zipper")
      var url =
        searchUrl +
        '?view=et-compare&type=product&options%5Bprefix%5D=last&q=' +
        encodeURIComponent(query);
      if (drawerSpinner) drawerSpinner.hidden = false;
      fetch(url)
        .then(function (response) {
          return response.ok ? response.json() : Promise.reject();
        })
        .then(function (data) {
          if (queryId !== activeQueryId) return;
          renderSearchResults(data.products || [], query);
        })
        .catch(function () {
          if (queryId !== activeQueryId) return;
          renderSearchResults([], query);
        })
        .finally(function () {
          if (queryId === activeQueryId && drawerSpinner) drawerSpinner.hidden = true;
        });
    }

    function openDrawer() {
      if (!drawer) return;
      if (drawerSearch) drawerSearch.value = '';
      // Kicks off (or reuses) the pool request; showPoolLoading swaps the
      // skeletons for brand tiles when it lands
      loadPool();
      showBrandGrid();
      drawer.classList.add('is-open');
      drawerOverlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('et-compare-drawer-open');
      if (drawerSearch) {
        // After the slide-in transition so the focus scroll doesn't fight it
        setTimeout(function () {
          drawerSearch.focus();
        }, 250);
      }
    }

    function closeDrawer() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawerOverlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('et-compare-drawer-open');
    }

    if (drawer) {
      if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);
      if (drawerCloseBtn) drawerCloseBtn.addEventListener('click', closeDrawer);
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
      });

      if (drawerBrands) {
        drawerBrands.addEventListener('click', function (event) {
          var brandBtn = event.target.closest('[data-compare-brand]');
          if (!brandBtn) return;
          var brand = brandBtn.getAttribute('data-compare-brand');
          if (drawerBackLabel) drawerBackLabel.textContent = brand;
          filterBikeTiles(function (tile) {
            return tile.getAttribute('data-vendor') === brand;
          });
          showResults('pool');
        });
      }

      if (drawerBackBtn) {
        drawerBackBtn.addEventListener('click', function () {
          if (drawerSearch) drawerSearch.value = '';
          showBrandGrid();
        });
      }

      if (drawerSearch) {
        drawerSearch.addEventListener('input', function () {
          clearTimeout(searchTimer);
          var query = drawerSearch.value.trim();
          if (query.length < 2) {
            showBrandGrid();
            return;
          }
          if (drawerBackLabel) drawerBackLabel.textContent = backLabelDefault;
          showResults('search');
          showSkeleton();
          searchTimer = setTimeout(function () {
            fetchCatalog(query);
          }, 300);
        });
      }

      drawer.addEventListener('click', function (event) {
        var pickBtn = event.target.closest('[data-compare-pick]');
        if (!pickBtn) return;
        onPick(pickBtn.getAttribute('data-compare-pick'), pickBtn.getAttribute('data-url'));
        closeDrawer();
      });
    }

    /* --- Layout ------------------------------------------------------------ */

    function refresh() {
      renderSlot();
      syncExtraRows();
      section.setAttribute('data-columns', String(1 + state.added.length));
      requestAnimationFrame(update);
    }

    /* --- Scroll progress bar + arrows --------------------------------------- */

    function columnScrollAmount() {
      var cell = scroller.querySelector('.et-product-compare__cell');
      return cell ? cell.offsetWidth + 16 : scroller.clientWidth * 0.8;
    }

    function update() {
      var maxScroll = scroller.scrollWidth - scroller.clientWidth;
      var overflowing = maxScroll > 1;

      if (track && thumb) {
        track.hidden = !overflowing;
        if (overflowing) {
          thumb.style.width = (scroller.clientWidth / scroller.scrollWidth) * 100 + '%';
          var trackSpace = track.clientWidth - thumb.offsetWidth;
          thumb.style.transform =
            'translateX(' + (scroller.scrollLeft / maxScroll) * trackSpace + 'px)';
        }
      }

      if (prevBtn && nextBtn) {
        prevBtn.hidden = !overflowing || scroller.scrollLeft <= 1;
        nextBtn.hidden = !overflowing || scroller.scrollLeft >= maxScroll - 1;
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scroller.scrollBy({ left: -columnScrollAmount(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scroller.scrollBy({ left: columnScrollAmount(), behavior: 'smooth' });
      });
    }

    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(update);
      observer.observe(scroller);
      if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    }

    /* --- Auto-populate default view: first pool bikes of the SAME product
       type as the current product (a fatbike PDP pre-fills fatbikes), in
       the collection's own sort order, topped up with other types when the
       pool lacks same-type bikes. Manual search/picking still spans every
       type and brand in the pool.

       Pool mode ships no candidates with the page — touching pool products
       in Liquid serializes each of them into Shopify's web-pixels bootstrap
       in the <head> (~200 KB on every bike PDP) — so the auto columns
       hydrate here from the lazy ?view=et-compare-pool payload once the
       section scrolls near. Fallback mode (merchant-picked list) still
       ships autoCandidates inline and renders them immediately. --- */

    refresh();

    (config.autoCandidates || []).forEach(function (candidate) {
      addColumn(candidate, true);
    });

    var autoQuota = config.autoCount || 0;
    var currentTypeLc = (config.currentType || '').trim().toLowerCase();
    var wantAutoHydrate =
      !!config.poolUrl && autoQuota > 0 && (config.autoCandidates || []).length === 0;

    function pickAutoFrom(sameTypeOnly) {
      for (var i = 0; i < poolOrder.length; i++) {
        if (state.added.length >= autoQuota) return;
        if (1 + state.added.length >= config.maxColumns) return;
        var item = poolOrder[i];
        var already = state.added.some(function (entry) {
          return entry.handle === item.handle;
        });
        if (already) continue;
        var sameType = (item.type || '').trim().toLowerCase() === currentTypeLc;
        if (sameTypeOnly !== sameType) continue;
        addColumn(item, true);
      }
    }

    function maybeFillAuto(finalPass) {
      if (!wantAutoHydrate || state.mode !== 'auto') return;
      pickAutoFrom(true);
      if (finalPass) pickAutoFrom(false);
      // An empty pool on the final pass means the fetch failed — stay armed
      // so a later load (e.g. opening the drawer) still fills the columns.
      if (state.added.length >= autoQuota || (finalPass && poolOrder.length > 0)) {
        wantAutoHydrate = false;
      }
    }

    if (wantAutoHydrate) {
      var hydrateAuto = function () {
        loadPool().then(function () {
          maybeFillAuto(true);
        });
      };
      if ('IntersectionObserver' in window) {
        var autoIo = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) {
                autoIo.disconnect();
                hydrateAuto();
                return;
              }
            }
          },
          { rootMargin: '600px 0px' }
        );
        autoIo.observe(section);
      } else {
        setTimeout(hydrateAuto, 2500);
      }
    }
  }

  function initAll() {
    document.querySelectorAll('.et-product-compare').forEach(initSection);
  }

  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
